/**
 * WebSocket Service for ElevenLabs Conversational AI API
 * Manages real-time voice conversations following ElevenLabs WebSocket protocol
 */

import { 
  ConversationInitiationMessage,
  UserAudioChunkMessage, 
  PongMessage,
  WebSocketMessageParser
} from '../models/websocketMessages.js';
import { ConnectionState } from '../models/connectionState.js';
import { CONNECTION_STATES } from '../enums/websocketMessageTypes.js';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.connectionState = new ConnectionState();
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
  }

  /**
   * Connect to ElevenLabs WebSocket server
   * @param {string} wsUrl - The WebSocket URL (signed URL from backend)
   * @param {Object} options - Connection options
   * @returns {Promise<void>}
   */
  async connect(wsUrl, options = {}) {
    return new Promise((resolve, reject) => {
      try {
        this.connectionState.setState(CONNECTION_STATES.CONNECTING);
        
        
        this.ws = new WebSocket(wsUrl);
        this.ws.binaryType = 'arraybuffer';

        this.ws.onopen = () => {
          this.connectionState.setState(CONNECTION_STATES.CONNECTED);
          this.reconnectAttempts = 0;
          
          // Send conversation initiation message if provided
          if (options.conversationConfig) {
            this.sendConversationInitiation(options.conversationConfig);
          } else {
          }
          
          this.emit('connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.connectionState.setError(`Connection error: ${error.message || 'Unknown error'}`);
          this.emit('error', this.connectionState.error);
          reject(error);
        };

        this.ws.onclose = (event) => {
          
          // Handle authentication-related error codes
          if (event.code === 3000 || event.reason?.includes('authorize') || event.reason?.includes('authentication')) {
            const errorMsg = `Authentication failed: ${event.reason}. Please check your API key or log in again.`;
            this.connectionState.setError(errorMsg);
            this.emit('error', errorMsg);
            return; // Don't attempt reconnection for auth errors
          }
          
          this.connectionState.setState(CONNECTION_STATES.DISCONNECTED);
          this.emit('disconnected', event);
          
          // Note: Reconnection should be handled at the application level since we need a fresh signed URL
        };
      } catch (error) {
        this.connectionState.setError(`Failed to connect: ${error.message}`);
        reject(error);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages according to ElevenLabs protocol
   * @param {MessageEvent} event
   */
  handleMessage(event) {
    if (event.data instanceof ArrayBuffer) {
      // Binary data should not be received according to ElevenLabs protocol
      return;
    }
    
    if (typeof event.data !== 'string') {
      return;
    }

    // Parse JSON message
    const message = WebSocketMessageParser.parse(event.data);
    if (!message) {
      return;
    }

    this.emit('message', message);

    // Handle specific message types according to ElevenLabs API
    if (WebSocketMessageParser.isConversationInitiationMetadata(message)) {
      const metadata = WebSocketMessageParser.extractConversationMetadata(message);
      this.connectionState.setConversationMetadata({
        conversationId: metadata.conversation_id,
        agentOutputAudioFormat: metadata.agent_output_audio_format,
        userInputAudioFormat: metadata.user_input_audio_format
      });
      this.emit('conversationReady', metadata);
      
    } else if (WebSocketMessageParser.isAudioResponse(message)) {
      const audioData = WebSocketMessageParser.extractAudioData(message);
      if (audioData) {
        this.emit('audioData', audioData);
      }
      
    } else if (WebSocketMessageParser.isUserTranscript(message)) {
      const transcript = WebSocketMessageParser.extractTranscript(message);
      this.emit('userTranscript', { text: transcript, speaker: 'user' });
      
    } else if (WebSocketMessageParser.isAgentResponse(message)) {
      const response = WebSocketMessageParser.extractTranscript(message);
      this.emit('agentResponse', { text: response, speaker: 'agent' });
      
    } else if (WebSocketMessageParser.isTentativeAgentResponse(message)) {
      const response = WebSocketMessageParser.extractTranscript(message);
      this.emit('tentativeResponse', { text: response, speaker: 'agent' });
      
    } else if (WebSocketMessageParser.isVADScore(message)) {
      const vadScore = WebSocketMessageParser.extractVADScore(message);
      this.emit('vadScore', vadScore);
      
    } else if (WebSocketMessageParser.isPing(message)) {
      const eventId = WebSocketMessageParser.extractPingEventId(message);
      this.sendPong(eventId);
      
    } else if (WebSocketMessageParser.isClientToolCall(message)) {
      const toolCall = WebSocketMessageParser.extractToolCall(message);
      this.emit('toolCall', toolCall);
      
    } else if (WebSocketMessageParser.isInterruption(message)) {
      this.emit('interruption', message.interruption_event);
      
    } else {
    }
  }

  /**
   * Send audio data to ElevenLabs server
   * @param {ArrayBuffer} audioData - PCM16 audio data
   */
  sendAudio(audioData) {
    if (!this.connectionState.isConnected()) {
      return false;
    }

    if (this.ws?.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      const audioMessage = new UserAudioChunkMessage(audioData);
      this.ws.send(JSON.stringify(audioMessage));
      return true;
    } catch (error) {
      console.error('❌ Error sending audio data:', error);
      this.connectionState.setError(`Failed to send audio: ${error.message}`);
      return false;
    }
  }

  /**
   * Send conversation initiation message
   * @param {Object} config - Conversation configuration
   */
  sendConversationInitiation(config) {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      const message = ConversationInitiationMessage.create(config);
      const messageString = JSON.stringify(message);
      this.ws.send(messageString);
      return true;
    } catch (error) {
      console.error('❌ Error sending conversation initiation:', error);
      return false;
    }
  }

  /**
   * Send pong response to ping
   * @param {number} eventId - Ping event ID
   */
  sendPong(eventId) {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      const pong = new PongMessage(eventId);
      this.ws.send(JSON.stringify(pong));
      return true;
    } catch (error) {
      console.error('Error sending pong:', error);
      return false;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
      this.connectionState.reset();
    }
  }




  /**
   * Add event listener
   * @param {string} event
   * @param {Function} callback
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Remove event listener
   * @param {string} event
   * @param {Function} callback
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to listeners
   * @param {string} event
   * @param {*} data
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        callback(data);
      });
    }
  }

  /**
   * Get connection state
   * @returns {ConnectionState}
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Check if WebSocket is connected and ready
   * @returns {boolean}
   */
  getIsConnected() {
    return this.connectionState.isConnected();
  }

  /**
   * Get conversation ID
   * @returns {string|null}
   */
  getConversationId() {
    return this.connectionState.conversationId;
  }
}

export const websocketService = new WebSocketService();
export default websocketService;